import { lineString } from "@turf/turf";
import length from "@turf/length";
import { LatLng } from "../model/viewModel/simpleTypes";
import { Dispatch, SetStateAction } from "react";
import { isValidCoordinate } from "./coordinateUtils";

// Type for the tool state, do not use outside of this file
type ToolState = {
  drawCable: boolean | { geometry: LatLng[] };
  // Add other properties as needed
  [key: string]: unknown;
};

/**
 * Gets the last point from a geometry array
 */
export const getLastPoint = (geometry: LatLng[]) => {
  if (!Array.isArray(geometry) || geometry.length === 0) return null;
  return geometry[geometry.length - 1];
};

/**
 * Calculate point at specific distance from a starting point towards a target point
 */
export const calculateOverridePoint = (fromPoint: LatLng, toPoint: LatLng, distance: number) => {
  if (!isValidCoordinate(fromPoint) || !isValidCoordinate(toPoint) || distance <= 0) {
    return toPoint;
  }

  try {
    const line = lineString([
      [fromPoint.lng, fromPoint.lat],
      [toPoint.lng, toPoint.lat],
    ]);
    const totalDistance = length(line, { units: "meters" });

    if (totalDistance === 0) return toPoint;

    const ratio = distance / totalDistance;
    const lat = fromPoint.lat - ratio * (fromPoint.lat - toPoint.lat);
    const lng = fromPoint.lng - ratio * (fromPoint.lng - toPoint.lng);

    return { lat, lng };
  } catch (error) {
    console.error("Error calculating override point:", error);
    return toPoint;
  }
};

/**
 * Creates wrapper functions to handle the dual nature of drawCable
 * (used as both boolean and object throughout the app)
 */
export const createDrawCableHelpers = (
  drawCable: boolean | { geometry: LatLng[] },
  toolState: ToolState,
  setToolState: Dispatch<SetStateAction<ToolState>>,
) => ({
  isDrawing: () => !!drawCable,

  getGeometry: () =>
    typeof drawCable === "object" && drawCable?.geometry ? drawCable.geometry : [],

  getCableObject: () => (typeof drawCable === "object" ? drawCable : { geometry: [] }),

  updateCable: (updatedCable: { geometry: LatLng[] }) => {
    setToolState({
      ...toolState,
      drawCable: updatedCable,
    });
  },
});

/**
 * Determines the point to add based on override length logic
 */
export const determinePointToAdd = (
  clickPosition: LatLng,
  overrideLength: number,
  geometry: LatLng[],
  getLastPoint: (geometry: LatLng[]) => LatLng | null,
) => {
  if (overrideLength > 0 && geometry.length > 0) {
    const lastPoint = getLastPoint(geometry);
    if (lastPoint) {
      return calculateOverridePoint(lastPoint, clickPosition, overrideLength);
    }
  }
  return clickPosition;
};

/**
 * Creates hint line positions for the preview line
 */
export const createHintLinePositions = (
  lastPoint: LatLng | null,
  currentPosition: LatLng | null,
) => {
  return lastPoint && currentPosition && isValidCoordinate(currentPosition)
    ? [lastPoint, currentPosition]
    : [];
};
